import React, { useEffect, useRef, useCallback } from 'react';
import { XIcon, StarIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const highlights = ['Your logo, colors & brand — fully applied', 'Your services, photos & real reviews', 'Live on your domain in 3–5 days', 'SEO-ready & mobile-perfect from day one'];
function ReclaimEmbed() {
  const ref = useRef<HTMLDivElement>(null);
  const injected = useRef(false);
  useEffect(() => {
    if (!ref.current || injected.current) return;
    injected.current = true;
    const script = document.createElement('script');
    script.src = 'https://meet.reclaimai.com/scripts/embed-scheduling-link.0.x.x.js';
    script.setAttribute('data-id', 'fe733afa-46c8-4024-b456-99864c397a4c');
    script.setAttribute('data-redirect', 'NONE');
    script.async = true;
    ref.current.appendChild(script);
  }, []);
  return <div ref={ref} style={{
    width: '100%'
  }} data-id="element-17" />;
}
export function BookingModal({
  isOpen,
  onClose
}: BookingModalProps) {
  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKey]);
  if (!isOpen) return null;
  return <div style={{
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(0,0,0,0.75)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px'
  }} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-18">
      <div style={{
      background: '#0F172A',
      border: '1px solid #1E293B',
      borderRadius: '20px',
      width: '100%',
      maxWidth: '860px',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative'
    }} data-id="element-19">
        {/* Amber top accent */}
        <div style={{
        height: '3px',
        background: '#F59E0B',
        borderRadius: '20px 20px 0 0'
      }} data-id="element-20" />

        {/* Close */}
        <button onClick={onClose} style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: '#1E293B',
        border: '1px solid #334155',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 10
      }} onMouseEnter={e => {
        (e.currentTarget as HTMLButtonElement).style.background = '#334155';
      }} onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.background = '#1E293B';
      }} data-id="element-21">
          <XIcon size={16} color="#94A3B8" data-id="element-22" />
        </button>

        {/* Header */}
        <div style={{
        padding: '32px 32px 20px'
      }} data-id="element-23">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '14px'
        }} data-id="element-24">
            <StarIcon size={11} color="#F59E0B" fill="#F59E0B" data-id="element-25" />
            <span style={{
            color: '#F59E0B',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase'
          }} data-id="element-26">
              You love what you see
            </span>
            <StarIcon size={11} color="#F59E0B" fill="#F59E0B" data-id="element-27" />
          </div>

          <h2 style={{
          color: '#FFFFFF',
          fontSize: 'clamp(22px, 4vw, 36px)',
          fontWeight: 900,
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          marginBottom: '10px'
        }} data-id="element-28">
            This Website Could Be{' '}
            <span style={{
            color: '#F59E0B'
          }} data-id="element-29">Yours.</span>
          </h2>

          <p style={{
          color: '#64748B',
          fontSize: '14px',
          lineHeight: 1.7,
          maxWidth: '560px',
          marginBottom: '18px'
        }} data-id="element-30">
            Everything you just explored — customised to your brand, your business, your city.{' '}
            <span style={{
            color: '#94A3B8',
            fontWeight: 500
          }} data-id="element-31">
              Let's talk for 15 minutes. No pitch deck. No pressure.
            </span>
          </p>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '7px'
        }} data-id="element-32">
            {highlights.map(item => <div key={item} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '7px'
          }} data-id="element-33">
                <CheckCircleIcon size={13} color="#F59E0B" strokeWidth={2} style={{
              flexShrink: 0,
              marginTop: '3px'
            }} data-id="element-34" />
                <span style={{
              color: '#94A3B8',
              fontSize: '13px',
              lineHeight: 1.5
            }} data-id="element-35">{item}</span>
              </div>)}
          </div>
        </div>

        {/* Divider */}
        <div style={{
        height: '1px',
        background: '#1E293B',
        margin: '0 32px'
      }} data-id="element-36" />

        {/* Booking */}
        <div style={{
        padding: '20px 32px 28px'
      }} data-id="element-37">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '14px',
          flexWrap: 'wrap',
          gap: '10px'
        }} data-id="element-38">
            <div data-id="element-39">
              <p style={{
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: '16px',
              marginBottom: '3px'
            }} data-id="element-40">
                Pick a time that works for you
              </p>
              <p style={{
              color: '#94A3B8',
              fontSize: '13px'
            }} data-id="element-41">
                15 min · Free · Your timezone detected automatically
              </p>
            </div>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }} data-id="element-42">
              <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#22C55E'
            }} data-id="element-43" />
              <span style={{
              color: '#64748B',
              fontSize: '12px'
            }} data-id="element-44">Available today</span>
            </div>
          </div>

          <div style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #1E293B'
        }} data-id="element-45">
            <ReclaimEmbed data-id="element-46" />
          </div>

          <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '18px',
          flexWrap: 'wrap',
          gap: '12px'
        }} data-id="element-47">
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }} data-id="element-48">
              <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: '#F59E0B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 900,
              fontSize: '13px',
              color: '#0F172A',
              flexShrink: 0,
              letterSpacing: '-0.5px'
            }} data-id="element-49">
                EM
              </div>
              <div data-id="element-50">
                <p style={{
                color: '#E2E8F0',
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: 1.2
              }} data-id="element-51">EasyMedia</p>
                <p style={{
                color: '#475569',
                fontSize: '12px'
              }} data-id="element-52">Web Design & WordPress Development</p>
              </div>
            </div>
            {/* UPDATED: correct EasyMedia Reclaim booking link */}
            <a href="https://app.reclaim.ai/m/easy-media/flexible-quick-meeting" target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '8px',
            background: '#F59E0B',
            color: '#0F172A',
            fontWeight: 700,
            fontSize: '13px',
            textDecoration: 'none'
          }} data-id="element-53">
              Open in new tab <ArrowRightIcon size={13} strokeWidth={2.5} data-id="element-54" />
            </a>
          </div>
        </div>
      </div>
    </div>;
}